import { useCallback, useMemo } from 'react';

import { ChessArrow, ChessUserArrow, ChessStockfishArrow } from '@exports/ChessBoard';
import { IColoredArrow, IColoredSquare, IPgnVariantMeta, IPgnMove } from '@libs/chess/pgn';

import { arrowColorToPgn, mapUserArrows } from '../utils/mapUserArrows';

interface IPgnEditor {
  selected?: IPgnMove;
  pgn: IPgnVariantMeta;
  updateMoveData: (m: IPgnMove, data: Partial<IPgnMove>) => void;
  updateVariantMeta: (variant: undefined, data: IPgnVariantMeta) => void;
}

/*
    ____,-------------------------------,____
    \   |              Хук              |   /
    /___|-------------------------------|___\
*/

export function usePgnArrows(
  { selected, pgn, updateMoveData, updateVariantMeta }: IPgnEditor,
  stockfishArrows?: ChessStockfishArrow[],
) {
  const { coloredArrows, coloredSquares } = selected ?? pgn;
  const arrows = useMemo<ChessArrow[]>(() => {
    const result: ChessArrow[] = mapUserArrows({ coloredArrows, coloredSquares });

    if (stockfishArrows) {
      result.push(...stockfishArrows);
    }

    return result;
  }, [coloredArrows, coloredSquares, stockfishArrows]);

  const onUserArrow = useCallback(
    (_: ChessUserArrow | null, next?: ChessUserArrow[]) => {
      const coloredArrows: IColoredArrow[] | undefined =
        next
          ?.filter((a) => a.from !== a.to)
          .map((a) => ({
            color: arrowColorToPgn(a.color),
            from: a.from,
            to: a.to,
          })) || undefined;
      const coloredSquares: IColoredSquare[] | undefined =
        next
          ?.filter((a) => a.from === a.to)
          .map((a) => ({
            color: arrowColorToPgn(a.color),
            square: a.from,
          })) || undefined;
      const data = {
        coloredArrows: coloredArrows?.length ? coloredArrows : undefined,
        coloredSquares: coloredSquares?.length ? coloredSquares : undefined,
      };
      if (selected) {
        updateMoveData(selected, data);
      } else {
        updateVariantMeta(undefined, data);
      }
    },
    [selected, updateMoveData, updateVariantMeta],
  );

  return { arrows, onUserArrow };
}
